import { useEffect, useRef, useState } from 'react';

// Define types first as the source of truth
export type SizeBreakpointKey = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
export type MotionBreakpointKey = 'motion-ok' | 'motion-not-ok';
export type ThemeBreakpointKey = 'theme-dark' | 'theme-light';
export type InteractionBreakpointKey = 'hover';

// Compose the complete breakpoint key type from category types
export type BreakpointKey =
  | SizeBreakpointKey
  | MotionBreakpointKey
  | ThemeBreakpointKey
  | InteractionBreakpointKey;

/**
 * Shared breakpoint definitions - Keep these in sync with your CSS @custom-media rules
 * @repo/ui/src/CSSBase/variables/custom-media.css
 */
export const BREAKPOINTS: Record<BreakpointKey, string> = {
  // Motion preferences
  'motion-ok': '(prefers-reduced-motion: no-preference)',
  'motion-not-ok': '(prefers-reduced-motion: reduce)',

  // Theme preferences
  'theme-dark': '(prefers-color-scheme: dark)',
  'theme-light': '(prefers-color-scheme: light)',

  // Interaction capabilities
  hover: '(hover: hover)',

  // Responsive breakpoints
  sm: '(width >= 640px)',
  md: '(width >= 768px)',
  lg: '(width >= 1024px)',
  xl: '(width >= 1280px)',
  xxl: '(width >= 1536px)',
} as const;

// Utility to get all keys of a specific breakpoint category
export const SIZE_BREAKPOINT_KEYS: readonly SizeBreakpointKey[] = [
  'sm',
  'md',
  'lg',
  'xl',
  'xxl',
] as const;
export const MOTION_BREAKPOINT_KEYS: readonly MotionBreakpointKey[] = [
  'motion-ok',
  'motion-not-ok',
] as const;
export const THEME_BREAKPOINT_KEYS: readonly ThemeBreakpointKey[] = [
  'theme-dark',
  'theme-light',
] as const;
export const INTERACTION_BREAKPOINT_KEYS: readonly InteractionBreakpointKey[] =
  ['hover'] as const;

/**
 * Performant, predictable media query hook
 * Uses explicit breakpoint definitions for reliability
 */
export function useMediaQuery(query: BreakpointKey): boolean;
export function useMediaQuery(query: string): boolean;
export function useMediaQuery(query: string): boolean {
  // Resolve query string
  const resolvedQuery =
    typeof query === 'string' && query in BREAKPOINTS
      ? BREAKPOINTS[query as BreakpointKey]
      : (query as string);

  // Initialize with current match state (SSR-safe)
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;

    try {
      return window.matchMedia(resolvedQuery).matches;
    } catch {
      return false;
    }
  });

  // Refs to avoid recreating MediaQueryList unnecessarily
  const mediaQueryRef = useRef<MediaQueryList | null>(null);
  const currentQueryRef = useRef<string>('');

  useEffect(() => {
    // Only create new MediaQueryList if query actually changed
    if (currentQueryRef.current !== resolvedQuery) {
      // Clean up previous listener
      if (mediaQueryRef.current) {
        mediaQueryRef.current.removeEventListener('change', handleChange);
      }

      // Create new MediaQueryList
      try {
        mediaQueryRef.current = window.matchMedia(resolvedQuery);
        currentQueryRef.current = resolvedQuery;

        // Update state immediately if needed
        const newMatches = mediaQueryRef.current.matches;
        if (newMatches !== matches) {
          setMatches(newMatches);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('Invalid media query:', resolvedQuery, error);
        setMatches(false);
        return;
      }
    }

    const mediaQuery = mediaQueryRef.current;
    if (!mediaQuery) return;

    // Event handler for media query changes
    function handleChange(event: MediaQueryListEvent) {
      setMatches(event.matches);
    }

    // Add listener
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup function
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [resolvedQuery, matches]);

  return matches;
}

/**
 * Type-safe hook for multiple media queries
 */
export function useMediaQueries<T extends Record<string, BreakpointKey>>(
  queries: T,
): Record<keyof T, boolean> {
  const results = {} as Record<keyof T, boolean>;

  Object.entries(queries).forEach(([key, query]) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    results[key as keyof T] = useMediaQuery(query);
  });

  return results;
}

// System preference hooks
export const useMotionOk = () => useMediaQuery('motion-ok');
export const useReducedMotion = () => useMediaQuery('motion-not-ok');
export const useDarkTheme = () => useMediaQuery('theme-dark');
export const useLightTheme = () => useMediaQuery('theme-light');
export const useHoverCapability = () => useMediaQuery('hover');

// Descriptive breakpoint hooks
export const useSmallScreen = () => useMediaQuery('sm');
export const useMediumScreen = () => useMediaQuery('md');
export const useLargeScreen = () => useMediaQuery('lg');
export const useExtraLargeScreen = () => useMediaQuery('xl');
export const useExtraExtraLargeScreen = () => useMediaQuery('xxl');

// Semantic size hooks (common patterns)
export const useIsMobile = () => !useMediumScreen(); // Below md
export const useIsTablet = () => {
  const md = useMediumScreen();
  const lg = useLargeScreen();
  return md && !lg; // md but not lg
};
export const useIsDesktop = () => useLargeScreen(); // lg and above

/**
 * Get all current size breakpoint states efficiently
 */
export function useBreakpoints(): Record<SizeBreakpointKey, boolean> {
  return useMediaQueries(
    Object.fromEntries(SIZE_BREAKPOINT_KEYS.map((key) => [key, key])) as Record<
      SizeBreakpointKey,
      SizeBreakpointKey
    >,
  );
}

/**
 * Get the current active breakpoint (largest matching)
 */
export function useCurrentBreakpoint(): 'xs' | SizeBreakpointKey {
  const breakpoints = useBreakpoints();

  // Check breakpoints in descending order (largest first)
  const orderedKeys = [
    ...SIZE_BREAKPOINT_KEYS,
  ].reverse() as SizeBreakpointKey[];

  for (const key of orderedKeys) {
    if (breakpoints[key]) {
      return key;
    }
  }

  return 'xs';
}
